// Church event page for the players: the music for one event, section by
// section, each piece with its chart, and the whole book at the top of a
// section. Public, reached only by its unguessable address; modelled on the
// Root of Life wedding page (see app/events/README.md).
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './EventPage.css';

interface EventItem { slot?: string; title: string; note?: string; file?: string; youtube?: string }
interface EventSection { name: string; time?: string; note?: string; book?: string; items: EventItem[] }
interface ChurchEvent {
  slug: string; address: string; title: string; date: string;
  place?: string; note?: string; sections: EventSection[];
  files: Record<string, string>;
  player?: string;
}

const client = axios.create({ baseURL: API_BASE_URL });

const longDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

// A section's name as it appears in the address (#thanksgiving-dinner), so a
// link can open straight on the dinner.
const slugOf = (name: string) =>
  name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const DownIcon = () => (
  <svg className="ev-ico" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
  </svg>
);

const EventPage: React.FC = () => {
  // /events/:address, or /events/:address/join/:token for a player's sign-in link.
  const { address, token } = useParams<{ address: string; token?: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<ChurchEvent | null>(null);
  const [missing, setMissing] = useState(false);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [badLink, setBadLink] = useState(false);
  // One section shown at a time (Jack, 2026-10-17: "service on one page and dinner on
  // another, not one below"); the address's #section picks which.
  const [hash, setHash] = useState(() => window.location.hash.replace(/^#/, ''));

  useEffect(() => {
    const onHash = () => setHash(window.location.hash.replace(/^#/, ''));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    let live = true;
    const path = `/events/${encodeURIComponent(address || '')}`;
    const load = () => client.get<ChurchEvent>(path)
      .then(r => { if (live) { setEvent(r.data); document.title = `${r.data.title}: Music`; } })
      .catch(err => {
        if (!live) return;
        if (err.response && err.response.status === 401) setNeedsSignIn(true);
        else setMissing(true);
      });
    if (token) {
      // A sign-in link: sign this browser in, then show the page at its plain address,
      // so the link is not left in the address bar or the history.
      client.post(`${path}/join`, { token })
        .then(() => { if (live) navigate(`/events/${address}${window.location.hash}`, { replace: true }); })
        .catch(() => { if (live) setBadLink(true); });
    } else {
      load();
    }
    return () => { live = false; };
  }, [address, token, navigate]);

  // A PDF's address names its build (?v=), so a phone never shows an old copy.
  const fileUrl = (file: string) =>
    `${API_BASE_URL}/events/${encodeURIComponent(event ? event.address : '')}/files/${encodeURIComponent(file)}?v=${event ? event.files[file] : ''}`;
  const has = (file?: string) => !!(file && event && event.files[file]);

  const sections = event ? event.sections : [];
  const active = sections.find(sec => slugOf(sec.name) === hash) || sections[0];
  const choose = (sec: EventSection) => {
    const slug = slugOf(sec.name);
    window.history.replaceState(null, '', `#${slug}`);
    setHash(slug);
    window.scrollTo({ top: 0 });
  };

  let body: React.ReactNode;
  if (missing) {
    body = <h1 className="ev-title">Not found</h1>;
  } else if (badLink) {
    body = (
      <>
        <h1 className="ev-title">This link did not work</h1>
        <p className="ev-muted">Please check that the whole link from the email was opened, or ask Jack for a new one.</p>
      </>
    );
  } else if (needsSignIn) {
    body = (
      <>
        <h1 className="ev-title">Music for the players</h1>
        <p className="ev-muted">This page is for the musicians. Open it with the personal link in your email; it keeps you signed in on this device for 60 days.</p>
      </>
    );
  } else if (!event) {
    body = <p className="ev-muted">Loading…</p>;
  } else {
    body = (
      <>
        <h1 className="ev-title">{event.title}</h1>
        <dl className="ev-facts">
          <div><dt>Date</dt><dd>{longDate(event.date)}</dd></div>
          {event.place && <div><dt>Place</dt><dd>{event.place}</dd></div>}
        </dl>
        {event.note && <p className="ev-muted">{event.note}</p>}

        {sections.length > 1 && (
          <nav className="ev-tabs" role="tablist" aria-label="Parts of the day">
            {sections.map(sec => (
              <button
                key={sec.name}
                type="button"
                role="tab"
                aria-selected={sec === active}
                className={`ev-tab${sec === active ? ' ev-tab-on' : ''}`}
                onClick={() => choose(sec)}
              >
                <span className="ev-tab-name">{sec.name}</span>
                {sec.time && <span className="ev-tab-time">{sec.time}</span>}
              </button>
            ))}
          </nav>
        )}

        {active && [active].map(sec => (
          <section className="ev-section" key={sec.name} role="tabpanel">
            {sections.length === 1 && (
              <h2 className="ev-cat">
                {sec.name}
                {sec.time && <span className="ev-count"> {sec.time}</span>}
              </h2>
            )}
            {sec.note && <p className="ev-muted">{sec.note}</p>}
            {has(sec.book) && (
              <div className="ev-books">
                <a className="ev-btn ev-btn-lg" href={fileUrl(sec.book!)} target="_blank" rel="noopener noreferrer">
                  <DownIcon />All the music in one book
                </a>
                <p className="ev-books-note">Individual charts below.</p>
              </div>
            )}
            {sec.items.length > 0 && (
              <ol className="ev-order">
                {sec.items.map((it, i) => (
                  <li key={`${sec.name}-${i}`}>
                    {it.slot && <div className="ev-moment">{it.slot}</div>}
                    <div className="ev-song">{it.title}</div>
                    {it.note && <div className="ev-note">{it.note}</div>}
                    {(has(it.file) || it.youtube) && (
                      <div className="ev-parts">
                        {has(it.file) && (
                          <a className="ev-btn ev-btn-sm" href={fileUrl(it.file!)} target="_blank" rel="noopener noreferrer">
                            <DownIcon />Chart
                          </a>
                        )}
                        {it.youtube && (
                          <a className="ev-btn ev-btn-sm" href={`https://www.youtube.com/watch?v=${encodeURIComponent(it.youtube)}`}
                            target="_blank" rel="noopener noreferrer">Listen</a>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </section>
        ))}
      </>
    );
  }

  return (
    <div className="ev-table">
      <main className="ev-sheet">
        <header className="ev-masthead">
          <span className="ev-brand">St. Luke's ~ San Lucas Music</span>
          {event && event.player && <span className="ev-who">Signed in as {event.player}</span>}
        </header>
        {body}
      </main>
    </div>
  );
};

export default EventPage;
